import { AppContext } from '@/contexts/app.context';
import { PlanUnit, TrainingPlan } from '@/generated';
import { Paper, Grid, Button, CardActions, Stack, IconButton, Tooltip, Fab, SxProps } from '@mui/material';
import { createContext, useContext, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { WorkoutUnitCard } from './WorkoutUnitCard';
import { DefaultContainer } from '@/components/DefaultContainer';
import AddIcon from '@mui/icons-material/Add';
import { AccessTime, CopyAll, Delete, FilterAlt, FilterAltOff, Key } from '@mui/icons-material';
import { DeleteDialog } from '@/components/DeleteDialog';
import EditIcon from '@mui/icons-material/Edit';
import { UpdatePlanModal } from '../UpdatePlanModal';
import { UpdateWorkoutUnitModal } from '../UpdateWorkoutUnitModal';
import { isPlanEditor } from '../utils';
import { usePlanQuery } from '@/hooks/query-hooks';
import { useUpdatePlan } from '@/hooks/plan-mutation.hooks';
import { PlanPermissionsElement } from '../PlanPermissionsIcon';
import moment from 'moment';
import { CopyWorkoutUnitModal } from '../CopyWorkoutUnitModal';
import { isSortable, useSortable } from '@dnd-kit/react/sortable';
import { DragDropProvider } from '@dnd-kit/react';
import { CurrentPlanStage } from '@/components/CurrentPlanStage';

const fabStyle = {
  position: 'fixed',
  bottom: 16,
  right: 16,
};

const fab = {
  color: 'primary' as 'primary',
  sx: fabStyle as SxProps,
  icon: <EditIcon />,
  label: 'Edit',
};

export const isOlderThanOneWeek = (isoDate?: string) => {
  if (!isoDate) return false;
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) {
    throw new Error('Invalid ISO date');
  }

  const oneWeekMs = 24 * 60 * 60 * 1000 * 7;
  return Date.now() - date.getTime() > oneWeekMs;
};

const getSortOrderFromLocalStorage = () => JSON.parse(localStorage.getItem('workoutPlansSortOrder') ?? JSON.stringify(false));
const writeSortOrderToLocalStorage = (sortByDate: boolean) => localStorage.setItem('workoutPlansSortOrder', JSON.stringify(sortByDate));

const getFilterUnitsOlderThanOneWeek = () => JSON.parse(localStorage.getItem('filteUnitsOlderThanOneWeek') ?? JSON.stringify(false));
const writeFilterUnitsOlderThanOneWeek = (filterUnits: boolean) => localStorage.setItem('filteUnitsOlderThanOneWeek', JSON.stringify(filterUnits));

export type PlanContextType = {
  groupId?: string;
  setGroupId: (groupId?: string) => void;
  workoutId?: string;
  setWorkoutId: (workoutId?: string) => void;
  plan?: TrainingPlan;
  updatePlan: (plan?: TrainingPlan) => Promise<void>;
};

export const getCurrentWorkout = (ctx: PlanContextType) => {
  return ctx.plan?.units.find((g) => g.idClient === ctx.groupId)?.workouts.find((w) => w.idClient === ctx.workoutId);
};

export const getCurrentGroup = (ctx: PlanContextType) => {
  return ctx.plan?.units.find((g) => g.idClient === ctx.groupId);
};

export const PlanContext = createContext<PlanContextType>({ setGroupId: () => {}, updatePlan: async () => {}, setWorkoutId: () => {} });

export const Plan = () => {
  const { planId } = useParams();
  const context = useContext(AppContext);
  const { plan } = usePlanQuery(planId);
  const localUser = context.state?.userProfile;
  const navigate = useNavigate();
  const [createGroupDialogOpen, setCreateGroupDialogOpen] = useState<boolean>(false);
  const [showPlanEditModal, setShowPlanEditModal] = useState<boolean>(false);
  const [sortByDate, setSortByDate] = useState<boolean>(getSortOrderFromLocalStorage());
  const [filterUnitsOlderThanOneWeek, setFilterUnitsOlderThanOneWeek] = useState<boolean>(getFilterUnitsOlderThanOneWeek());

  if (plan) context.setExtraTitle(' - ' + plan?.title);

  return (
    <DefaultContainer>
      {plan && (
        <UpdatePlanModal
          plan={plan}
          setShow={(show) => {
            setShowPlanEditModal(show);
          }}
          show={showPlanEditModal}
        />
      )}
      <>
        {plan && (
          <UpdateWorkoutUnitModal
            plan={plan}
            show={createGroupDialogOpen}
            setShow={async (open) => {
              setCreateGroupDialogOpen(open);
            }}
          />
        )}
        <Paper sx={{ my: 1, zIndex: 1, padding: 1, position: 'sticky', top: { xs: 60, sm: 65 } }}>
          <Grid sx={{ alignContent: 'center', alignItems: 'center' }} container gap={2} direction={'row'}>
            <Stack sx={{ gap: 1 }}>
              <Stack sx={{ alignItems: 'center' }} gap={2} direction={'row'}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    navigate('/plans');
                  }}
                >
                  &lt;&nbsp;&nbsp;Plans
                </Button>
                <CurrentPlanStage name={plan?.title} />
              </Stack>
            </Stack>
            <PlanPermissionsElement plan={plan} />
          </Grid>
        </Paper>

        <Paper sx={{ my: 1, zIndex: 1, padding: 1, position: 'sticky', top: { xs: 60, sm: 65 } }}>
          <Grid sx={{ alignContent: 'center', alignItems: 'center' }} container gap={2} direction={'row'}>
            {isPlanEditor(plan, localUser) && (
              <Stack sx={{ gap: 1 }}>
                <Tooltip title={'Create new workout unit. - A workout unit contains workouts from different sets.'}>
                  <IconButton
                    sx={{ alignSelf: 'flex-start' }}
                    onClick={() => {
                      setCreateGroupDialogOpen(true);
                    }}
                  >
                    <AddIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            )}

            <Stack sx={{ gap: 1 }}>
              {!sortByDate ? (
                <Tooltip title={'Order by key. Click to change.'}>
                  <IconButton
                    sx={{ alignSelf: 'flex-start' }}
                    onClick={() => {
                      setSortByDate(!sortByDate);
                      writeSortOrderToLocalStorage(!sortByDate);
                    }}
                  >
                    <Key />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title={'Ordered by time. Click to change.'}>
                  <IconButton
                    sx={{ alignSelf: 'flex-start' }}
                    onClick={() => {
                      setSortByDate(!sortByDate);
                      writeSortOrderToLocalStorage(!sortByDate);
                    }}
                  >
                    <AccessTime />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
            <Tooltip title={'Filter Units that are older than one week'}>
              <IconButton
                onClick={() => {
                  setFilterUnitsOlderThanOneWeek(!filterUnitsOlderThanOneWeek);
                  writeFilterUnitsOlderThanOneWeek(!filterUnitsOlderThanOneWeek);
                }}
              >
                {filterUnitsOlderThanOneWeek ? <FilterAlt /> : <FilterAltOff />}
              </IconButton>
            </Tooltip>
          </Grid>
        </Paper>

        <WorkoutUnitList plan={plan} sortByDate={sortByDate} />
      </>
      {isPlanEditor(plan, localUser) && (
        <Fab
          onClick={() => {
            setShowPlanEditModal(true);
          }}
          sx={fab.sx}
          aria-label={fab.label}
          color={fab.color}
        >
          {fab.icon}
        </Fab>
      )}
    </DefaultContainer>
  );
};

export const WorkoutUnitList = (props: { plan?: TrainingPlan; sortByDate: boolean }) => {
  const { plan, sortByDate } = props;
  const updatePlanMutation = useUpdatePlan();
  const context = useContext(AppContext);
  const localUser = context.state?.userProfile;

  return (
    <DragDropProvider
      onDragEnd={async (event) => {
        const { source } = event.operation;

        if (!isSortable(source)) return;

        const { initialIndex, index } = source;

        if (initialIndex === index) return;

        if (!plan) return;

        const units = [...plan.units];
        const [moved] = units.splice(source.initialIndex, 1);
        units.splice(source.index, 0, moved);
        await updatePlanMutation.mutateAsync({ notifyUsers: false, plan: { ...plan, units } });
      }}
    >
      <Grid container direction={'row'} spacing={2}>
        {[...(plan?.units ?? [])]
          .sort((a, b) => {
            return sortByDate ? sortByTrainingDate(a, b) : 0;
          })
          .filter((u) => (getFilterUnitsOlderThanOneWeek() ? !isOlderThanOneWeek(u.trainingDateIso) : true))
          .map((g: PlanUnit, i) => (
            <PlanWorkoutUnitElement sortingAllowed={!sortByDate && isPlanEditor(plan, localUser)} index={i} id={g.idClient} plan={plan} key={g.idClient} group={g} />
          ))}
      </Grid>
    </DragDropProvider>
  );
};

const PlanWorkoutUnitElement = (props: { id: string; index: number; sortingAllowed: boolean; plan?: TrainingPlan; group: PlanUnit }) => {
  const { group, plan, sortingAllowed, id, index } = props;
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showCopyDialog, setShowCopyDialog] = useState(false);
  const context = useContext(AppContext);
  const localUser = context.state?.userProfile;
  const updatePlanMutation = useUpdatePlan();
  const { ref } = useSortable({ id, index, disabled: !sortingAllowed });

  return (
    <>
      <DeleteDialog
        title={`Really delete ${group.name}`}
        text={`Really delete ${group.name}`}
        open={showDeleteDialog}
        handleClose={async (deleted) => {
          if (deleted) {
            try {
              if (plan) {
                await updatePlanMutation.mutateAsync({ notifyUsers: false, plan: { ...plan, units: plan.units.filter((g) => g.idClient !== group.idClient) } });
              }
            } catch (e) {
              console.error(e);
            }
          }
          setShowDeleteDialog(false);
        }}
      />
      {plan && (
        <UpdateWorkoutUnitModal
          plan={plan}
          workoutUnit={group}
          show={showEditDialog}
          setShow={(show) => {
            setShowEditDialog(show);
          }}
        />
      )}

      {plan && (
        <CopyWorkoutUnitModal
          plan={plan}
          workoutUnit={group}
          show={showCopyDialog}
          setShow={(show) => {
            setShowCopyDialog(show);
          }}
        />
      )}

      <Grid ref={ref} size={{ xs: 12, md: 4 }}>
        <WorkoutUnitCard planId={plan?.id} timeout={1000} workoutUnit={group}>
          <CardActions sx={{ justifyContent: 'flex-end', flex: 0.1 }}>
            {isPlanEditor(plan, localUser) && (
              <>
                <Tooltip title={'Copy this workout unit.'}>
                  <IconButton
                    onClick={async () => {
                      setShowCopyDialog(true);
                    }}
                  >
                    <CopyAll />
                  </IconButton>
                </Tooltip>
                <Tooltip title={'Edit this workout unit.'}>
                  <IconButton
                    onClick={async () => {
                      setShowEditDialog(true);
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title={'Delete this workout unit.'}>
                  <IconButton
                    onClick={async () => {
                      setShowDeleteDialog(true);
                    }}
                  >
                    <Delete />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </CardActions>
        </WorkoutUnitCard>
      </Grid>
    </>
  );
};

const sortByTrainingDate = (a: PlanUnit, b: PlanUnit) => {
  if (!a.trainingDateIso) return 1; // a is undefined → move to bottom
  if (!b.trainingDateIso) return -1; // b is undefined → move to bottom

  return moment(b.trainingDateIso).valueOf() - moment(a.trainingDateIso).valueOf();
};
